/**
 * 路由注册表 —— 前端路径的唯一来源（规格 docs/specs/frontend-paper-redesign/SPEC.md §4.2）。
 *
 * 路由器、目录轨、⌘K、移动端底栏全部从这里派生。此前路径在四处各写一份，
 * 于是出现了指向重定向的「仪表盘」、落在重定向上的移动端「首页/选股」。
 * 路径与导航元数据只在这里定义；route-loaders.ts 提供按 RoutePath 穷尽的页面加载器。
 * 元数据不导入页面，避免 Page / PageHeader 的类型依赖穿透到全站。
 *
 * 分段（D3，2026-09-19 用户裁决）：盘前 / 盘中 / 复盘 / 研究 / 系统，系统永远最后。
 * 顺序一旦定下不按时段、热度、最近访问自动重排 —— 位置不变是肌肉记忆的前提。
 */
import {
  Activity,
  Award,
  BellRing,
  BookOpen,
  Bot,
  BrainCircuit,
  Briefcase,
  CalendarDays,
  CandlestickChart,
  ClipboardList,
  Coins,
  Crosshair,
  Database,
  FileSearch,
  Flame,
  History,
  Landmark,
  Layers,
  LayoutDashboard,
  ListChecks,
  ListTree,
  Map,
  Newspaper,
  NotebookPen,
  PenLine,
  Radar,
  Repeat,
  Repeat2,
  Scale,
  ScanEye,
  ScrollText,
  Server,
  Settings,
  Settings2,
  Sparkles,
  Spline,
  Star,
  Sunrise,
  Target,
  Thermometer,
  Timer,
  TrendingUp,
  Wallet,
  Waypoints,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export type Segment = 'pre' | 'intraday' | 'review' | 'research' | 'system';

export const SEGMENTS: readonly Segment[] = ['pre', 'intraday', 'review', 'research', 'system'];

export const SEGMENT_ICON: Record<Segment, LucideIcon> = {
  pre: Sunrise,
  intraday: Activity,
  review: ClipboardList,
  research: BookOpen,
  system: Settings,
};

export const SEGMENT_LABEL: Record<Segment, string> = {
  pre: '盘前',
  intraday: '盘中',
  review: '复盘',
  research: '研究',
  system: '系统',
};

interface RouteMetadata {
  path: string;
  name: string;
  icon: LucideIcon;
  /** 缺省 = 不进目录轨（个股详情这类带参数的页面），仍进 ⌘K 以外的路由表 */
  segment?: Segment;
  /** ⌘K 用：拼音全拼、首字母、旧名、英文路径。至少一项纯 ASCII 小写 */
  keywords: readonly string[];
  /** 移动端底栏的短名；有值即进底栏，最多 4 个 */
  mobile?: string;
}

const ROUTE_DEFINITIONS = [
  // ── 盘前
  { path: '/premarket-digest', name: '盘前情报', segment: 'pre', icon: Newspaper, keywords: ['panqianqingbao', 'pqqb', 'premarket'] },
  { path: '/policy-calendar', name: '政策日历', segment: 'pre', icon: CalendarDays, keywords: ['zhengcerili', 'zcrl', 'policy'] },
  { path: '/auction-radar', name: '竞价雷达', segment: 'pre', icon: Timer, keywords: ['jingjialeida', 'jjld', 'auction'] },

  // ── 盘中
  { path: '/youzi-workbench', name: '游资体系', segment: 'intraday', icon: Scale, keywords: ['youzitixi', 'yztx', '大游资', 'youzi-workbench'] },
  { path: '/intraday-mainline', name: '盘中主线', segment: 'intraday', icon: Timer, keywords: ['panzhongzhuxian', '涨停扩散'] },
  { path: '/intraday-watch', name: '盘中盯盘', segment: 'intraday', icon: Activity, keywords: ['panzhongdingpan', 'pzdp', 'intraday-watch', '盯盘'] },
  { path: '/fund-flow', name: '资金监控', segment: 'intraday', icon: Wallet, keywords: ['zijinjiankong', 'zjjk', '资金流', 'fund'], mobile: '资金' },
  { path: '/drawing-desk', name: '画线工作台', segment: 'intraday', icon: Spline, keywords: ['huaxiangongzuotai', 'hxgzt', 'drawing', 'kxian', 'k线'] },
  { path: '/pattern-scan', name: '形态选股', segment: 'intraday', icon: PenLine, keywords: ['xingtaixuangu', 'txxg', 'pattern', 'ma99'] },
  { path: '/mm-cycle', name: '庄家操盘', segment: 'intraday', icon: Repeat2, keywords: ['zhuangjiacaopan', 'zjcp', 'mm-cycle', 'xichou', 'paifa', '吸筹', '派发', '量价分析'] },
  { path: '/reversal-radar', name: '反转雷达', segment: 'intraday', icon: Crosshair, keywords: ['reversal', 'fanzhuan', '四线放量'] },
  { path: '/reversal-review', name: '突破信号复盘', segment: 'review', icon: ClipboardList, keywords: ['突破复盘', '密集突破', 'reversal-review', 'T+10', '概念聚集'] },
  { path: '/smart-picker-v2', name: '智能选股', segment: 'intraday', icon: Crosshair, keywords: ['zhinengxuangu', 'znxg', 'picker'] },
  { path: '/leader-selection', name: '龙头股选股', segment: 'intraday', icon: Award, keywords: ['longtouguxuangu', 'ltgxg', 'leadership', '龙头'] },
  { path: '/leader-library', name: '龙头股库', segment: 'intraday', icon: Database, keywords: ['longtouguku', 'ltgk', 'leadership-library', '龙头评分'] },
  { path: '/signals', name: '缠论信号广场', segment: 'intraday', icon: Sparkles, keywords: ['chanlunxinhao', 'clxhgc', 'xinhao', 'signals', '1b', '2b'], mobile: '信号' },
  { path: '/portfolio', name: '仓位管理', segment: 'intraday', icon: Briefcase, keywords: ['cangweiguanli', 'cwgl', 'chicang', 'portfolio'], mobile: '持仓' },
  { path: '/sentinel-v2', name: '持仓哨兵', segment: 'intraday', icon: Target, keywords: ['chicangshaobing', 'ccsb', 'sentinel'] },
  { path: '/anomaly', name: '量价异动', segment: 'intraday', icon: Zap, keywords: ['liangjiayidong', 'ljyd', 'anomaly'] },

  // ── 复盘
  { path: '/ma99-review', name: 'MA99 信号复盘', segment: 'review', icon: History, keywords: ['ma99', 'ma99fupan', '信号复盘'] },
  { path: '/emotion-ladder', name: '情绪梯队', segment: 'review', icon: Thermometer, keywords: ['qingxutidui', 'qxtd', 'emotion'] },
  { path: '/lhb', name: '龙虎榜', segment: 'review', icon: Landmark, keywords: ['longhubang', 'lhb', 'youzixiwei', 'yzxw', 'hotmoney', 'xiwei', '席位', '游资'] },
  { path: '/heat-radar', name: '热度雷达', segment: 'review', icon: Flame, keywords: ['reduleida', 'rdld', 'heat'] },
  { path: '/sector-scan', name: '板块扫描', segment: 'review', icon: Radar, keywords: ['bankuaisaomiao', 'bksm', 'sector'] },
  { path: '/sector-structure', name: '板块结构', segment: 'review', icon: Layers, keywords: ['bankuaijiegou', 'bkjg', 'darvas', 'zhongshu'] },
  { path: '/anomaly-timeline', name: '异动时间线', segment: 'review', icon: ListTree, keywords: ['yidongshijianxian', 'ydsjx', 'timeline'] },
  { path: '/limit-up-review', name: '涨停复盘', segment: 'review', icon: Award, keywords: ['zhangtingfupan', 'ztfp', 'limitup'] },
  { path: '/recent-limit-ladder', name: '近期连板股', segment: 'review', icon: Award, keywords: ['jinqilianban', 'lianban', '近期连板', 'recent-limit-ladder'] },
  { path: '/limit-up-pattern', name: '涨停模式', segment: 'review', icon: TrendingUp, keywords: ['zhangtingmoshi', 'ztms', 'limitup'] },
  { path: '/rotation-desk', name: '板块轮动 · 主线', segment: 'review', icon: Layers, keywords: ['bankuailundong', 'bkld', 'zhuxian', 'zx', 'rotation', '主线'] },

  // ── 研究
  { path: '/patri-research', name: 'Patri 交易体系', segment: 'research', icon: Scale, keywords: ['patri', 'patri-research', 'jiaoyiyanjiu', '交易研究'] },
  { path: '/trendline-system', name: '趋势线交易体系', segment: 'research', icon: Spline, keywords: ['qushixianjiaoyi', 'qsxjy', 'trendline-system', '趋势线', '超跌反转'] },
  { path: '/index-valuation', name: '平均市盈率', segment: 'research', icon: TrendingUp, keywords: ['pingjunshiyinglv', 'pjsyl', 'pe', '估值', '百分位', 'valuation'] },
  { path: '/stock-vault', name: '标的库', segment: 'research', icon: Database, keywords: ['biaodiku', 'bdk', 'vault', '板块研习', '知识地图', '陌生板块'] },
  { path: '/news-lens', name: '新闻透镜', segment: 'research', icon: ScanEye, keywords: ['xinwentoujing', 'xwtj', 'news'] },
  { path: '/deep-equity', name: '个股深研', segment: 'research', icon: FileSearch, keywords: ['gegushenyan', 'ggsy', 'deep'] },
  { path: '/domain-atlas', name: '领域图谱', segment: 'research', icon: Map, keywords: ['lingyutupu', 'lytp', 'atlas', 'chanyelian'] },
  { path: '/sector-wave-v2', name: '板块龙头基因回测 v2', segment: 'research', icon: Repeat, keywords: ['bankuailongtou', 'bklt', 'longtou', 'swl'] },
  { path: '/edge', name: '跨市场传导', segment: 'research', icon: Waypoints, keywords: ['kuashichangchuandao', 'kscd', 'edge'] },
  { path: '/research-agent', name: '投研专家 Agent', segment: 'research', icon: BrainCircuit, keywords: ['touyanzhuanjia', 'tyzj', 'agent'] },
  { path: '/paper-ledger', name: '下注台账', segment: 'research', icon: NotebookPen, keywords: ['xiazhutaizhang', 'xztz', 'ledger'] },
  { path: '/backtest-board', name: '实发通知证据', segment: 'research', icon: ListChecks, keywords: ['celuechengjidan', 'clcjd', 'backtest', 'huice', '回测', '回避'] },
  { path: '/signal-statistics', name: '信号统计', segment: 'research', icon: ListChecks, keywords: ['xinhaotongji', 'signal-statistics', '全部组统计', '生命周期', '收益'] },
  { path: '/a-share-backtest', name: 'A 股策略回测', segment: 'research', icon: Scale, keywords: ['agu', 'celuehuice', 'account', 'backtest', '资金账户', '策略回测'] },
  { path: '/strategy-scorecard', name: '每周策略成绩单', segment: 'research', icon: ListChecks, keywords: ['celuechengjidan', 'weekly', 'scorecard', '策略比较'] },
  { path: '/trading-system', name: '交易系统', segment: 'research', icon: Scale, keywords: ['jiaoyixitong', 'jyxt', 'trading'] },
  { path: '/chan-rsi', name: '缠论合约', segment: 'research', icon: ScrollText, keywords: ['chanlunheyue', 'clhy', 'rsi'] },

  // ── 系统（永远最后）
  { path: "/stock-descriptions", name: "个股简介数据库", segment: "system", icon: Database, keywords: ["gegujianjie", "ggjj", "stock-descriptions", "公司介绍"] },
  { path: "/classifications", name: "板块/概念分类数据库", segment: "system", icon: ListTree, keywords: ["fenlei", "classifications", "bankuaigainian"] },
  { path: '/watchlist', name: '自选股管理', segment: 'system', icon: Star, keywords: ['zixuangu', 'zxg', 'watchlist'], mobile: '自选' },
  { path: '/data-center', name: '数据中心', segment: 'system', icon: Server, keywords: ['shujuzhongxin', 'sjzx', 'data'] },
  { path: '/notify-center', name: '通知中心', segment: 'system', icon: BellRing, keywords: ['tongzhizhongxin', 'tzzx', 'feishu', 'notify'] },
  { path: '/settings/signals', name: '信号推送设置', segment: 'system', icon: Settings2, keywords: ['tuisongshezhi', 'tssz', 'settings'] },
  { path: '/ai-assets', name: 'AI 资产', segment: 'system', icon: Bot, keywords: ['aizichan', 'aizc', 'assets'] },
  { path: '/dashboard', name: '仪表盘', segment: 'system', icon: LayoutDashboard, keywords: ['yibiaopan', 'ybp', 'dashboard'] },

  // ── 不进目录的路由
  { path: '/stock/:symbol', name: '个股详情', icon: CandlestickChart, keywords: ['gegu', 'stock'] },
  { path: '/ai-history', name: 'AI 分析历史', icon: History, keywords: ['aifenxilishi', 'history'] },
  { path: '/admin/signal-economy', name: '信号经济', icon: Coins, keywords: ['xinhaojingji', 'economy'] },
] as const satisfies readonly RouteMetadata[];

export type RoutePath = (typeof ROUTE_DEFINITIONS)[number]['path'];
export type RouteEntry = Omit<RouteMetadata, 'path'> & { path: RoutePath };
export const ROUTES: RouteEntry[] = ROUTE_DEFINITIONS.map((route) => ({
  ...route,
  keywords: [...route.keywords],
}));

/** 首页落点（2026-09-18 用户点名为资金监控） */
export const HOME_PATH = '/intraday-watch?source=strength&taxonomy=concept';

/**
 * 旧路径 → 新路径。只允许在这里写重定向；重定向保留查询参数与 hash
 * （此前顶栏板块搜索跳 /sector-v2?sector=… 时参数被 <Navigate> 丢掉）。
 */
export const REDIRECTS: Record<string, string> = {
  '/streak-sentiment': '/emotion-ladder',
  '/smart-picker': '/smart-picker-v2',
  '/recent-mainline': '/rotation-desk',
  '/sector-rotation': '/rotation-desk',
  '/sector-v2': '/rotation-desk',
  '/limit-hold-open': '/limit-up-pattern',
  // 「游资席位」→「龙虎榜」（2026-09-20，LHB Desk M3）。
  // 旧页的立论「『谁在买』比『买了多少』信息量大」已被回放证伪，
  // 席位维度与分段判决整体取代它。
  '/hot-money': '/lhb',
};

export const NAV_ROUTES = ROUTES.filter((r): r is RouteEntry & { segment: Segment } => !!r.segment);

export const MOBILE_ROUTES = ROUTES.filter((r) => r.mobile);

/** 当前路径属于哪个注册项（前缀匹配，/smart-picker-v2/x 归 /smart-picker-v2） */
export function matchRoute(pathname: string): RouteEntry | undefined {
  return NAV_ROUTES.find((r) => pathname === r.path || pathname.startsWith(`${r.path}/`));
}

/** 页头用：目录里的页面按前缀匹配，不进目录的页面（AI 分析历史等）按精确路径匹配 */
export function findRoute(pathname: string): RouteEntry | undefined {
  return matchRoute(pathname) ?? ROUTES.find((r) => !r.path.includes(':') && r.path === pathname);
}
